import './report-featured-card.scss'

import _ from 'lodash'
import { take } from 'rxjs/operators'
import { defineComponent } from 'vue'
import { faExclamationTriangle, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'

import { SearchFeatured, getTranslatedString } from '@skinsoft/types'
import { itemSelectors, specimenSelectors } from '@skinsoft/selectors'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { ActionType, States, Touchable } from '@skinsoft/ui'
import { Media } from '../item-card/media'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'

export const ReportfeaturedCard = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sMedia: Media,
        sTouchable: Touchable,
    },
    props: ['reportFeatured', 'targetRouteName', 'titleClickAction'],
    template: require('./report-featured-card.html'),
    data() {
        return {
            state: States.PENDING as States,
            concernedItemSummary: "",
            editIcon: faExternalLinkAlt,
            // Icons
            warningIcon: faExclamationTriangle,
        }
    },
    computed: {
        reportReason(): string {
            return getTranslatedString(_.get(this.reportFeatured.reportReason, 'secondaryValue'), this.getAppLocale())
        },
    },
    watch: {
        reportFeatured: {
            handler(reportFeatured: SearchFeatured) {
                this.unsubscribeFrom('concerned-item');
                this.state = reportFeatured ? States.READY : States.PENDING;
                if (reportFeatured) {
                    this.buildConcernedItemSummary();
                }
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        isReady() {
            return this.state === States.READY;
        },
        async openReportDocument() {
            const response = await this.$conditionReportPDFTransferHelper.downloadOriginalPDFFile(this.reportFeatured.firstMedia).toPromise()
            const fileName = `${this.reportFeatured.referenceNumber}.pdf`;
            // Notify dl success
            if (response) {
                this.download(fileName, response)
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('listReportScreen.closed.downloadSuccess', { docTitle: fileName }),
                    variant: "success"
                })

            }
            // Notify dl failure
            else {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('listReportScreen.closed.downloadFailure'),
                    variant: "danger"
                })
            }
        },
        /**
         * Launches the downloading of a file
         * @param fileName
         * @param text
         */
        download(fileName, text: Blob) {

            const data = window.URL.createObjectURL(text)
            const element = document.createElement('a')
            element.style.display = 'none'
            element.href = data;
            element.setAttribute('download', fileName)
            document.body.appendChild(element)
            element.click()
            document.body.removeChild(element)

            setTimeout(() => {
                window.URL.revokeObjectURL(data);
            }, 100);

        },
        editReport() {
            if (this.titleClickAction && this.titleClickAction.action === ActionType.EDIT) {
                // delay to let button animation occure
                setTimeout(() => {
                    this.$router.push(
                        {
                            name: this.titleClickAction.targetRouteName,
                            params: {
                                reportId: this.reportFeatured.id
                            },
                        });
                }, 300);
            }
        },
        onTitleClicked() {
            this.editReport()
        },
        buildConcernedItemSummary() {
            const itemId = this.reportFeatured.involvedItem;
            const itemType = this.reportFeatured.involvedItemType;
            if (itemId) {
                switch (itemType) {
                    case "specimen":
                        this.subscribeTo(this.$specimenDataStore.fetchOne(itemId).pipe(take(1)),
                            (specimen) => {
                                this.concernedItemSummary = specimenSelectors.firstIdentifier(specimen) ?? "";
                                const itemTitle = specimenSelectors.title(specimen, this.getAppLocale()) as string;
                                if (itemTitle) {
                                    this.concernedItemSummary += ` (${itemTitle})` as string;
                                }
                            },
                            'concerned-item'
                        )
                        break
                    default:
                        this.subscribeTo(this.$itemDataStore.fetchOne(itemId).pipe(take(1)),
                            (item) => {
                                this.concernedItemSummary = itemSelectors.identifier(item, this.getAppLocale());
                                const itemTitle = itemSelectors.title(item, this.getAppLocale()) as string;
                                if (itemTitle) {
                                    this.concernedItemSummary += ` (${itemTitle})` as string;
                                }
                            },
                            'concerned-item'
                        )
                        break
                }
            }
        },
    },
})
